import * as React from "react";
import { makeStyles } from "@material-ui/styles";
import { Error } from "@material-ui/icons";
import { Button } from "@material-ui/core";
import { colors, greys, helpers } from "@styles/color.styles";
import { fontFamilyHelvetica } from "@styles/type.styles";
import classnames from "classnames";

type Sentiment = "info" | "warning" | "pro";

export interface SentimentIconProps {
  sentiment: Sentiment;
}

export interface CalloutCardProps {
  sentiment: Sentiment;
  content: { header?: string; body?: React.ReactNode };
  action?: { text: React.ReactNode; onClick: () => void };
  withIcon?: boolean;
}

const useStyles = makeStyles({
  container: {
    border: `1px solid ${greys.cloudyBlue}`,
    borderRadius: 8,
    padding: "10px 13px",
    display: "flex",
    gap: 8,
  },
  container__warning: {
    border: `1px solid ${colors.peach.darker}`,
    background: helpers.opacity(colors.peach.lightest, 0.6),
  },
  container__pro: {
    border: `1px solid ${helpers.opacity(colors.blueSky.darker, 0.5)}`,
    background: helpers.opacity(colors.blueSky.lightest, 0.25),
  },

  content: {
    fontFamily: fontFamilyHelvetica,
  },
  header: {
    margin: "0px 0px 8px",
    fontWeight: "bold",
    lineHeight: 1.2,
    fontSize: `${14 / 16}rem`,
    color: colors.blueDusk.darker,
  },
  body: {
    margin: "0px",
    lineHeight: 1.5,
    fontSize: `${14 / 16}rem`,

    "& > p": {
      margin: "0px",
      lineHeight: 1.5,
      fontSize: `${14 / 16}rem`,
    },

    "& > a, * > a": {
      fontWeight: "bold",
      textDecoration: "none",
      "&: hover": {
        cursor: "pointer",
      },
    },
  },
  body__pro: {
    fontSize: `${12 / 16}rem`,

    "& > p": {
      fontSize: `${12 / 16}rem`,
    },
  },

  content__warning: {
    color: colors.peach.darker,
    "& p, a": {
      color: colors.peach.darker,
    },
  },
  content__info: {
    color: greys.darkGreyBlue,
    "& a": {
      color: colors.green.darker,
      textDecoration: "underline",
    },
  },
  content__pro: {
    color: colors.blueSky.darker,
    "& p, a": {
      color: colors.blueSky.darker,
      fontSize: `${12 / 16}rem`,
    },
  },

  action: {
    color: greys.slate,
    border: `1px solid ${greys.slate}`,
    marginTop: 8,
    padding: "1px 8px",
    fontSize: `${12 / 16}rem`,
    fontFamily: fontFamilyHelvetica,
  },
  action__warning: {
    color: colors.peach.darker,
    border: `1px solid ${colors.peach.darker}`,
  },
  action__pro: {
    color: colors.blueSky.darker,
    border: `1px solid ${colors.blueSky.darker}`,
  },

  icon: {
    height: 16,
    width: 16,
    marginTop: 1,
    color: greys.slate,
  },

  icon__warning: {
    color: colors.peach.darker,
  },
  icon__info: {
    color: colors.blueSky.darker,
  },
});

const SentimentIcon = ({ sentiment }: SentimentIconProps) => {
  const classes = useStyles();

  if (sentiment === "warning") {
    return <Error className={classnames(classes.icon, classes.icon__warning)} />;
  }

  return null;
};

export const CalloutCard = ({ sentiment, content, withIcon, action }: CalloutCardProps) => {
  const classes = useStyles();

  return (
    <div
      className={classnames(classes.container, {
        [classes.container__warning]: sentiment === "warning",
        [classes.container__pro]: sentiment === "pro",
      })}
    >
      {withIcon && <SentimentIcon sentiment={sentiment} />}
      <div
        className={classnames(classes.content, {
          [classes.content__warning]: sentiment === "warning",
          [classes.content__info]: sentiment === "info",
          [classes.content__pro]: sentiment === "pro",
        })}
      >
        {content.header && <p className={classes.header}>{content.header}</p>}
        {content.body && (
          <div
            className={classnames(classes.body, {
              [classes.body__pro]: sentiment === "pro",
            })}
          >
            {content.body}
          </div>
        )}
        {action && (
          <Button
            className={classnames(classes.action, {
              [classes.action__warning]: sentiment === "warning",
              [classes.action__pro]: sentiment === "pro",
            })}
            size="small"
            onClick={action.onClick}
          >
            {action.text}
          </Button>
        )}
      </div>
    </div>
  );
};
